// src/pages/ProjectsPage.jsx
import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import Navbar from "../components/Navbar";
import { useNavigate, useLocation } from "react-router-dom";
import DatePicker from "react-datepicker";
import TruncateWithTooltip from "../components/TruncateWithTooltip";
import "react-datepicker/dist/react-datepicker.css";
import {
  AiOutlineFolder,
  AiOutlineFile,
  AiOutlinePlus,
  AiOutlineDelete,
  AiOutlineEdit,
  AiOutlineDownload,
  AiOutlinePlayCircle,
  AiOutlinePauseCircle,
} from "react-icons/ai";
import { useFolderFile } from "../context/FolderFileContext";
import { getUserRoleFromToken } from "../utils/getUserRoleFromToken";

const token0 = localStorage.getItem("token");
const userRole = getUserRoleFromToken(token0);
const isAdminFromToken = userRole === "admin";

const API = "/api";

// Input/rename hard limit (validation)
const NAME_MAX = 48;
// Visible characters in name cell
const DISPLAY_MAX = 36;

const formatLocalYMD = (d) =>
  d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}` : undefined;

// Build a superset of date params so whichever your backend expects will be present.
function buildDateParams(dateType, [start, end]) {
  const from = formatLocalYMD(start);
  const to = formatLocalYMD(end);
  const isCreated = (dateType || "").startsWith("created");
  return {
    // generic
    from,
    to,

    // legacy / common
    date_from: from,
    date_to: to,
    date_type: dateType,

    // explicit variants (some APIs use these)
    ...(isCreated
      ? {
          created_from: from,
          created_to: to,
          created_at_from: from,
          created_at_to: to,
        }
      : {
          updated_from: from,
          updated_to: to,
          updated_at_from: from,
          updated_at_to: to,
        }),
  };
}

// --- Size formatter: binary units, keep ≤3 integer digits, de-DE separators
function formatSize(bytes) {
  if (bytes == null || isNaN(bytes)) return "-";
  if (bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB", "PB"];
  let i = 0;
  let val = bytes;
  while (val >= 1024 && i < units.length - 1) {
    val /= 1024;
    i++;
  }
  let decimals = 0;
  if (val < 10) decimals = 2;
  else if (val < 100) decimals = 1;
  else decimals = 0;

  const nf = new Intl.NumberFormat("de-DE", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return `${nf.format(val)} ${units[i]}`;
}

/* ----------------------------------------------------------------------------
   TooltipPortal (for tooltips that shouldn't get clipped by table/overflow)
-----------------------------------------------------------------------------*/
function TooltipPortal({ open, anchorEl, children }) {
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    const update = () => {
      if (!anchorEl || !open) return;
      const rect = anchorEl.getBoundingClientRect();
      setPos({
        top: rect.bottom + 6,
        left: Math.max(8, rect.left),
      });
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [anchorEl, open]);

  if (!open || !anchorEl) return null;
  return createPortal(
    <div
      style={{ position: "fixed", top: pos.top, left: pos.left, zIndex: 10000, maxWidth: "60ch" }}
      className="bg-black bg-opacity-90 text-white text-xs rounded py-1 px-2 whitespace-pre shadow-lg pointer-events-none"
    >
      {children}
    </div>,
    document.body
  );
}

/* ----------------------------------------------------------------------------
   ActiveBadge (replaces old GreenDot) — hooks at top, then early return
-----------------------------------------------------------------------------*/
function ActiveBadge({ users = [] }) {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);

  if (!users || users.length === 0) return null;

  const list = users.map((u) => `${u.first_name} ${u.last_name} (${u.email})`).join("\n");

  return (
    <span
      ref={anchorRef}
      className="relative inline-flex items-center ml-2"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      tabIndex={0}
    >
      <span
        aria-hidden="true"
        className="inline-block w-2.5 h-2.5 rounded-full bg-green-500 border-2 border-white shrink-0"
      />
      {users.length > 1 && (
        <span className="ml-1 text-[10px] leading-none px-1 rounded-full bg-green-600 text-white font-medium shrink-0">
          {users.length}
        </span>
      )}

      <TooltipPortal open={open} anchorEl={anchorRef.current}>
        {list}
      </TooltipPortal>
    </span>
  );
}

function ProjectsPage({
  isAdmin,          // optional (when embedded in admin workers tab)
  adminUserId,
  adminContext,
  fullWidth,
  navigateTo,
  setNavigateTo,
}) {
  const token = localStorage.getItem("token");
  const location = useLocation();
  const {
    folders,
    files,
    fetchFolder,
    fetchFile,
    addFolders,
    addFiles,
    updateFolder,
    updateFile,
    removeFolder,
    removeFile,
  } = useFolderFile();

  // Upload name validation helpers
  const uploadErrorsRef = useRef([]);
  const isValidFolderName = (name) => !!name && name.length <= NAME_MAX;
  const getFileBase = (filename) => {
    const dot = filename.lastIndexOf(".");
    return dot > 0 ? filename.slice(0, dot) : filename;
  };
  const isValidFileBase = (base) => !!base && base.length <= NAME_MAX;

  const [folderList, setFolderList] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [parentId, setParentId] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [search, setSearch] = useState("");
  const [searchScope, setSearchScope] = useState("current"); // "current" | "all"
  const [typeFilter, setTypeFilter] = useState(""); // "" | "folders" | "files"
  const showFolders = typeFilter === "" || typeFilter === "folders";
  const showFiles = typeFilter === "" || typeFilter === "files";
  const [dateType, setDateType] = useState("created_at");
  const [dateRange, setDateRange] = useState([null, null]);
  const [workingOn, setWorkingOn] = useState(null);
  const [highlightId, setHighlightId] = useState(null);
  const [newFolderName, setNewFolderName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [error, setError] = useState("");
  const [dragActive, setDragActive] = useState(false);

  const [highlightActive, setHighlightActive] = useState(false);
  const highlightTimeoutRef = useRef(null);

  const [folderSort, setFolderSort] = useState({ col: "name", order: "asc" });
  const [fileSort, setFileSort] = useState({ col: "name", order: "asc" });

  // Pagination state
  const [folderPage, setFolderPage] = useState(1);
  const [filePage, setFilePage] = useState(1);
  const [folderPageSize, setFolderPageSize] = useState(20);
  const [filePageSize, setFilePageSize] = useState(20);

  // Editable page inputs
  const [folderPageStr, setFolderPageStr] = useState("1");
  const [filePageStr, setFilePageStr] = useState("1");

  const fileInputRef = useRef();
  const folderInputRef = useRef();
  const mainRef = useRef();

  // Green dot maps
  const [activeWorkMap, setActiveWorkMap] = useState({ folders: {}, files: {} });
  const [activeFolderCascade, setActiveFolderCascade] = useState({});

  // Active-only toggle (Workers → Projects)
  const [activeOnly, setActiveOnly] = useState(false);

  // SPA navigation
  const [navTarget, setNavTarget] = useState(null);
  const navKeyRef = useRef(0);

  // Intervals
  const refreshInterval = 800;

  const navigate = useNavigate();

  function getMyUserId() {
    try {
      const t = localStorage.getItem("token");
      if (!t) return null;
      // base64url → base64
      const payload = JSON.parse(
        atob((t.split(".")[1] || "").replace(/-/g, "+").replace(/_/g, "/"))
      );
      // accept common keys
      return (
        payload?.id ??
        payload?.user_id ??
        payload?.userId ??
        payload?.user?.id ??
        payload?.sub ??
        null
      );
    } catch {
      return null;
    }
  }
  const myUserId = getMyUserId();

  // ---------- SSE live updates (admin workers)
  useEffect(() => {
    if (adminContext !== "workers") return;
    const apiBase = API.replace("/api", "");
    let source;
    try {
      source = new EventSource(`${apiBase}/api/work/stream?token=${token}`);
    } catch {
      source = null;
    }
    const handleActive = (e) => {
      try {
        const rows = JSON.parse(e.data);
        const projMap = {};
        const fileMap = {};
        rows.forEach((entry) => {
          if (entry.project_id) {
            if (!projMap[entry.project_id]) projMap[entry.project_id] = [];
            if (!projMap[entry.project_id].some((u) => u.id === entry.user_id))
              projMap[entry.project_id].push({
                id: entry.user_id,
                first_name: entry.first_name,
                last_name: entry.last_name,
                email: entry.email,
              });
          }
          if (entry.file_id) {
            if (!fileMap[entry.file_id]) fileMap[entry.file_id] = [];
            if (!fileMap[entry.file_id].some((u) => u.id === entry.user_id))
              fileMap[entry.file_id].push({
                id: entry.user_id,
                first_name: entry.first_name,
                last_name: entry.last_name,
                email: entry.email,
              });
          }
        });
        setActiveWorkMap({ folders: projMap, files: fileMap });
      } catch {}
    };
    if (source) {
      source.addEventListener("active", handleActive);
      source.onerror = () => {};
    }
    return () => {
      try {
        source?.close();
      } catch {}
    };
  }, [adminContext, token]);

  const handleWorkingOnClick = async () => {
    try {
      // If we’re already inside AdminWorkersPage’s embedded Projects, stay here.
      if (adminContext === "workers") {
        if (workingOn.parentToNav !== undefined && workingOn.parentToNav !== null) {
          setParentId(workingOn.parentToNav);
          if (!workingOn.isFolder) {
            setHighlightId(workingOn.item.id); // highlight file
            setHighlightActive(true);
            if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
            highlightTimeoutRef.current = setTimeout(() => {
              setHighlightActive(false);
              setHighlightId(null);
            }, 5000);
          }
        }
        return;
      }

      const auth = { headers: { Authorization: `Bearer ${token}` } };
      const ownerFromProject = (pd) =>
        pd?.user_id ?? pd?.owner_id ?? pd?.ownerId ?? pd?.owner?.id ?? pd?.user?.id ?? null;

      let ownerId = null;
      let projectIdForNav = workingOn.parentToNav ?? null; // where to OPEN
      let fileId = null; // what to HIGHLIGHT if file
      let folderId = null; // what to HIGHLIGHT if folder
      let foreign = false;

      if (workingOn.isFolder && workingOn.item?.id) {
        // FOLDER: open its PARENT and highlight the folder itself
        folderId = workingOn.item.id;
        try {
          const p = await axios.get(`${API}/projects/${workingOn.item.id}`, auth);
          ownerId = ownerFromProject(p.data);
          projectIdForNav = p.data?.parent_id ?? projectIdForNav; // parent folder
        } catch (e) {
          if ([403, 404].includes(e?.response?.status)) foreign = true;
        }
      } else if (!workingOn.isFolder && workingOn.item?.id) {
        // FILE: open its CONTAINING FOLDER and highlight the file
        fileId = workingOn.item.id;
        try {
          const f = await axios.get(`${API}/files/${workingOn.item.id}`, auth);
          const projId = f.data?.project_id ?? null;
          if (projId != null) {
            projectIdForNav = projId; // containing folder (do NOT change to its parent)
            try {
              const p = await axios.get(`${API}/projects/${projId}`, auth);
              ownerId = ownerFromProject(p.data);
            } catch (e) {
              if ([403, 404].includes(e?.response?.status)) foreign = true;
            }
          }
        } catch (e) {
          if ([403, 404].includes(e?.response?.status)) foreign = true;
        }
      }

      if (!foreign && ownerId && myUserId) {
        foreign = String(ownerId) !== String(myUserId);
      }

      const path = foreign ? "/admin/workers" : "/projects";
      navigate(path, {
        state: {
          ownerId: foreign ? ownerId : undefined, // lets AdminWorkers preselect owner
          projectId: projectIdForNav ?? null, // folder to OPEN
          fileId, // highlight if file
          folderId, // highlight if folder
        },
      });
    } catch {
      // Fallback: do the local jump if anything fails
      if (workingOn.parentToNav !== undefined && workingOn.parentToNav !== null) {
        setParentId(workingOn.parentToNav);
      }
    }
  };

  // ---------- Fallback polling
  const pollActiveWorkMap = useCallback(() => {
    if (adminContext === "workers") {
      axios
        .get(`${API}/work/active-map`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => {
          const projMap = {};
          const fileMap = {};
          res.data.forEach((entry) => {
            if (entry.project_id) {
              if (!projMap[entry.project_id]) projMap[entry.project_id] = [];
              if (!projMap[entry.project_id].some((u) => u.id === entry.user_id))
                projMap[entry.project_id].push({
                  id: entry.user_id,
                  first_name: entry.first_name,
                  last_name: entry.last_name,
                  email: entry.email,
                });
            }
            if (entry.file_id) {
              if (!fileMap[entry.file_id]) fileMap[entry.file_id] = [];
              if (!fileMap[entry.file_id].some((u) => u.id === entry.user_id))
                fileMap[entry.file_id].push({
                  id: entry.user_id,
                  first_name: entry.first_name,
                  last_name: entry.last_name,
                  email: entry.email,
                });
            }
          });
          setActiveWorkMap({ folders: projMap, files: fileMap });
        })
        .catch(() => {});
    }
  }, [adminContext, token]);

  useEffect(() => {
    if (adminContext !== "workers") return;
    pollActiveWorkMap(); // initial
    const interval = setInterval(pollActiveWorkMap, refreshInterval * 20);
    return () => clearInterval(interval);
  }, [adminContext, pollActiveWorkMap]);

  // ---------- Green dot cascade (async)
  useEffect(() => {
    if (adminContext !== "workers") {
      setActiveFolderCascade({});
      return;
    }
    let cancelled = false;
    const run = async () => {
      if (searchScope === "all") {
        const cascade = {};
        Object.entries(activeWorkMap.folders).forEach(([folderId, users]) => {
          cascade[Number(folderId)] = users;
        });
        if (!cancelled) setActiveFolderCascade(cascade);
        return;
      }
      const cascade = {};
      const ensureFolder = async (fid) => {
        if (folders[fid]) return folders[fid];
        try {
          const f = await fetchFolder(fid, token);
          return f || null;
        } catch {
          return null;
        }
      };
      const ensureFile = async (fileId) => {
        if (files[fileId]) return files[fileId];
        try {
          const f = await fetchFile(fileId, token);
          return f || null;
        } catch {
          return null;
        }
      };
      const climbParents = async (fid) => {
        const chain = [];
        let current = fid;
        while (current) {
          const f = await ensureFolder(current);
          if (!f) break;
          chain.unshift(f.id);
          current = f.parent_id;
        }
        return chain;
      };
      for (const [folderIdStr, users] of Object.entries(activeWorkMap.folders)) {
        const folderId = Number(folderIdStr);
        const chain = await climbParents(folderId);
        chain.forEach((fid) => {
          if (!cascade[fid]) cascade[fid] = [];
          users.forEach((u) => {
            if (!cascade[fid].some((x) => x.id === u.id)) cascade[fid].push(u);
          });
        });
      }
      for (const [fileIdStr, users] of Object.entries(activeWorkMap.files)) {
        const fileId = Number(fileIdStr);
        const file = await ensureFile(fileId);
        if (!file || !file.project_id) continue;
        const chain = await climbParents(Number(file.project_id));
        chain.forEach((fid) => {
          if (!cascade[fid]) cascade[fid] = [];
          users.forEach((u) => {
            if (!cascade[fid].some((x) => x.id === u.id)) cascade[fid].push(u);
          });
        });
      }
      if (!cancelled) setActiveFolderCascade(cascade);
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [activeWorkMap, folders, files, adminContext, searchScope, fetchFolder, fetchFile, token]);

  // ---------- SPA navigation from other tabs (now honors folderId)
  useEffect(() => {
    let nav = null;
    if (
      location.state &&
      (location.state.projectId !== undefined ||
        location.state.fileId !== undefined ||
        location.state.folderId !== undefined)
    ) {
      nav = {
        parentId: location.state.projectId ?? null,
        highlightId:
          location.state.fileId ?? location.state.folderId ?? location.state.projectId,
        navKey: Date.now() + Math.random(),
      };
    } else if (
      typeof navigateTo === "object" &&
      (navigateTo.projectId !== undefined ||
        navigateTo.fileId !== undefined ||
        navigateTo.folderId !== undefined)
    ) {
      nav = {
        parentId: navigateTo.projectId ?? null,
        highlightId: navigateTo.fileId ?? navigateTo.folderId ?? navigateTo.projectId,
        navKey: Date.now() + Math.random(),
      };
      if (setNavigateTo) setNavigateTo({});
    }
    if (nav) {
      setNavTarget(nav);
      setParentId(nav.parentId);
      navKeyRef.current = nav.navKey;
    }
  }, [location.state, navigateTo, setNavigateTo]);

  useEffect(() => {
    if (!navTarget) return;
    const timeout = setTimeout(() => {
      setHighlightId(navTarget.highlightId);
      setHighlightActive(true);
      if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
      highlightTimeoutRef.current = setTimeout(() => {
        setHighlightActive(false);
        setHighlightId(null);
      }, 5000);
      setNavTarget(null);
      if (window.history && window.history.replaceState) {
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }, 400);
    return () => clearTimeout(timeout);
  }, [navTarget, folderList, fileList, parentId]);

  // ---------- Data fetch
  const fetchData = useCallback(async () => {
    setError("");
    try {
      const dateParams = buildDateParams(dateType, dateRange);

      const projectsParams = {
        parent_id: parentId === null ? "" : parentId,
        search: search || undefined,
        all: searchScope === "all" ? 1 : undefined,
        sort: folderSort.col,
        order: folderSort.order,
        ...(adminContext ? { context: adminContext } : {}),
        ...(adminContext && adminUserId ? { user: adminUserId } : {}),
        ...dateParams,
      };

      let pf = await axios.get(`${API}/projects`, {
        headers: { Authorization: `Bearer ${token}` },
        params: projectsParams,
      });
      pf = pf.data;
      if (searchScope === "all" && parentId) {
        pf = pf.filter((f) => f.id !== parentId);
      }
      addFolders(pf);

      let foldersArr = showFolders ? pf : [];
      if (adminContext === "workers" && foldersArr.length) {
        const act = foldersArr
          .filter((f) => activeFolderCascade[f.id]?.length)
          .sort(byFolderSort(folderSort));
        const inact = foldersArr
          .filter((f) => !activeFolderCascade[f.id]?.length)
          .sort(byFolderSort(folderSort));
        foldersArr = [...act, ...inact];
      }
      if (adminContext === "workers" && activeOnly) {
        foldersArr = foldersArr.filter((f) =>
          searchScope === "all"
            ? !!activeWorkMap.folders[f.id]
            : !!activeFolderCascade[f.id]?.length
        );
      }
      setFolderList(foldersArr);

      const filesParams = {
        project_id: parentId || undefined,
        search: search || undefined,
        all: searchScope === "all" ? 1 : undefined,
        sort: fileSort.col,
        order: fileSort.order,
        ...(adminContext ? { context: adminContext } : {}),
        ...(adminContext && adminUserId ? { user: adminUserId } : {}),
        ...dateParams,
      };

      let fl = [];
      if (parentId || searchScope === "all") {
        const resp = await axios.get(`${API}/files`, {
          headers: { Authorization: `Bearer ${token}` },
          params: filesParams,
        });
        fl = resp.data;
      }
      addFiles(fl);

      let filesArr = showFiles ? fl : [];
      if (adminContext === "workers" && filesArr.length) {
        const act = filesArr
          .filter((f) => !!activeWorkMap.files[f.id])
          .sort(byFileSort(fileSort));
        const inact = filesArr
          .filter((f) => !activeWorkMap.files[f.id])
          .sort(byFileSort(fileSort));
        filesArr = [...act, ...inact];
      }
      if (adminContext === "workers" && activeOnly) {
        filesArr = filesArr.filter((f) => !!activeWorkMap.files[f.id]);
      }

      setFileList(filesArr);
    } catch {
      setError("Could not fetch folders/files.");
      setFolderList([]);
      setFileList([]);
    }
  }, [
    parentId,
    search,
    searchScope,
    showFolders,
    showFiles,
    dateType,
    dateRange,
    token,
    addFolders,
    addFiles,
    folderSort,
    fileSort,
    adminUserId,
    adminContext,
    activeFolderCascade,
    activeWorkMap,
    activeOnly,
  ]);

  function byFolderSort(s) {
    return (a, b) => {
      let x = a[s.col],
        y = b[s.col];
      if (s.order === "desc") [x, y] = [y, x];
      if (x < y) return -1;
      if (x > y) return 1;
      return 0;
    };
  }
  function byFileSort(s) {
    return (a, b) => {
      let x = a[s.col],
        y = b[s.col];
      if (s.col === "size") {
        x = a.size || 0;
        y = b.size || 0;
      }
      if (s.order === "desc") [x, y] = [y, x];
      if (x < y) return -1;
      if (x > y) return 1;
      return 0;
    };
  }

  // ---------- periodic refresh
  useEffect(() => {
    fetchData();
    fetchBreadcrumbs();
    fetchWorkingOn();
    const interval = setInterval(() => {
      fetchData();
      fetchBreadcrumbs();
      fetchWorkingOn();
    }, refreshInterval);
    return () => clearInterval(interval);
  }, [
    parentId,
    search,
    searchScope,
    typeFilter,
    dateType,
    dateRange,
    folderSort.col,
    folderSort.order,
    fileSort.col,
    fileSort.order,
    adminUserId,
    adminContext,
    fetchData,
  ]);

  // Reset pagination when filters change
  useEffect(() => {
    setFolderPage(1);
    setFilePage(1);
  }, [
    parentId,
    search,
    searchScope,
    typeFilter,
    dateType,
    dateRange,
    folderSort.col,
    folderSort.order,
    fileSort.col,
    fileSort.order,
    activeOnly,
  ]);

  // Clamp current page if data shrinks
  useEffect(() => {
    const maxF = Math.max(1, Math.ceil(folderList.length / folderPageSize));
    if (folderPage > maxF) setFolderPage(maxF);
  }, [folderList.length, folderPage, folderPageSize]);
  useEffect(() => {
    const maxF = Math.max(1, Math.ceil(fileList.length / filePageSize));
    if (filePage > maxF) setFilePage(maxF);
  }, [fileList.length, filePage, filePageSize]);

  // keep editable inputs in sync with current page
  useEffect(() => setFolderPageStr(String(folderPage)), [folderPage]);
  useEffect(() => setFilePageStr(String(filePage)), [filePage]);

  const fetchBreadcrumbs = useCallback(async () => {
    if (!parentId) return setBreadcrumbs([]);
    const path = [];
    let current = parentId;
    while (current) {
      let folder = folders[current];
      if (!folder) {
        folder = await fetchFolder(current, token);
      }
      if (folder) {
        path.unshift(folder);
        current = folder.parent_id;
      } else break;
    }
    setBreadcrumbs(path);
  }, [parentId, folders, fetchFolder, token]);

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    if (newFolderName.length > NAME_MAX) {
      setError(`Folder name too long. Max ${NAME_MAX} characters.`);
      return;
    }
    try {
      const res = await axios.post(
        `${API}/projects`,
        { name: newFolderName, parent_id: parentId ?? null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      updateFolder(res.data);
      setNewFolderName("");
      fetchData();
    } catch {
      setError("Failed to create folder");
    }
  };

  // ---------- Helpers for drag & drop (reused by folder input flow)
  const childrenCache = useRef(new Map()); // parentId -> [{id,name}]
  const getChildrenOfParent = useCallback(
    async (pid) => {
      if (pid === undefined) return [];
      const key = String(pid ?? "root-null");
      if (childrenCache.current.has(key)) return childrenCache.current.get(key);
      try {
        const resp = await axios.get(`${API}/projects`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            parent_id: pid ?? "",
            sort: "name",
            order: "asc",
            context: adminContext || undefined,
            user: adminUserId || undefined,
          },
        });
        const arr = Array.isArray(resp.data) ? resp.data : [];
        childrenCache.current.set(key, arr);
        return arr;
      } catch {
        return [];
      }
    },
    [token, adminContext, adminUserId]
  );

  const addChildToCache = (pid, folderObj) => {
    const key = String(pid ?? "root-null");
    const arr = childrenCache.current.get(key) || [];
    childrenCache.current.set(key, [...arr, folderObj]);
  };

  const createChildFolder = useCallback(
    async (name) => {
      if (!name && name !== "") return null;
      if (!isValidFolderName(name)) {
        uploadErrorsRef.current.push(
          `Skipped creating folder "${name}": name must be ≤ ${NAME_MAX} characters.`
        );
        return null;
      }
      try {
        const created = await axios.post(
          `${API}/projects`,
          { name, parent_id: parentId ?? null },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        updateFolder(created.data);
        addChildToCache(parentId ?? null, created.data);
        return created.data;
      } catch {
        return null;
      }
    },
    [parentId, token, updateFolder]
  );

  const ensureChildByExactName = useCallback(
    async (pid, name) => {
      if (!isValidFolderName(name)) {
        uploadErrorsRef.current.push(
          `Skipped creating folder "${name}": name must be ≤ ${NAME_MAX} characters.`
        );
        return null;
      }
      const siblings = await getChildrenOfParent(pid ?? null);
      const existing = siblings.find(
        (f) => (f.name || "").toLowerCase() === (name || "").toLowerCase()
      );
      if (existing) return existing;
      try {
        const created = await axios.post(
          `${API}/projects`,
          { name, parent_id: pid ?? null },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        updateFolder(created.data);
        addChildToCache(pid ?? null, created.data);
        return created.data;
      } catch {
        return null;
      }
    },
    [getChildrenOfParent, token, updateFolder]
  );

  const ensureNestedFolderPath = useCallback(
    async (baseParentId, relativeDirPath) => {
      const basePid = baseParentId ?? null;
      const norm = (relativeDirPath || "").replace(/\\/g, "/").replace(/^\/+|\/+$/g, "");
      if (!norm) return { id: basePid };
      const parts = norm.split("/").filter(Boolean);
      let currentParent = basePid;
      for (const name of parts) {
        // eslint-disable-next-line no-await-in-loop
        const child = await ensureChildByExactName(currentParent, name);
        if (!child) return null;
        currentParent = child.id;
      }
      return { id: currentParent };
    },
    [ensureChildByExactName]
  );

  async function walkDirHandle(dirHandle, group, prefix = "") {
    // eslint-disable-next-line no-restricted-syntax
    for await (const entry of dirHandle.values()) {
      if (entry.kind === "file") {
        const file = await entry.getFile();
        group.files.push({ file, relativePath: prefix + entry.name });
      } else if (entry.kind === "directory") {
        group.dirs.add(prefix + entry.name + "/");
        // eslint-disable-next-line no-await-in-loop
        await walkDirHandle(entry, group, prefix + entry.name + "/");
      }
    }
  }

  function readGroupsWebkit(items) {
    return new Promise((resolve) => {
      const groups = [];
      const defaultGroup = { rootName: null, files: [], dirs: new Set() };

      if (!items || !items.length || !items[0].webkitGetAsEntry) {
        resolve({ groups, defaultGroup });
        return;
      }

      let pending = 0;
      const finish = () => {
        if (pending === 0) resolve({ groups, defaultGroup });
      };

      const readAll = (reader, group, prefix) => {
        reader.readEntries((entries) => {
          if (entries.length) {
            entries.forEach((e) => handleChild(e, group, prefix));
            readAll(reader, group, prefix);
          } else {
            pending--;
            finish();
          }
        });
      };

      const handleChild = (entry, group, prefix = "") => {
        if (entry.isFile) {
          pending++;
          // eslint-disable-next-line no-undef
          entry.file((file) => {
            group.files.push({ file, relativePath: prefix + file.name });
            pending--;
            finish();
          });
        } else if (entry.isDirectory) {
          group.dirs.add(prefix + entry.name + "/");
          const reader = entry.createReader();
          pending++;
          readAll(reader, group, prefix + entry.name + "/");
        }
      };

      for (let i = 0; i < items.length; i++) {
        const top = items[i].webkitGetAsEntry && items[i].webkitGetAsEntry();
        if (!top) continue;
        if (top.isFile) {
          pending++;
          // eslint-disable-next-line no-undef
          top.file((file) => {
            defaultGroup.files.push({ file, relativePath: "" });
            pending--;
            finish();
          });
        } else if (top.isDirectory) {
          const group = { rootName: top.name, files: [], dirs: new Set() };
          const reader = top.createReader();
          pending++;
          readAll(reader, group, "");
          groups.push(group);
        }
      }
      finish();
    });
  }

  async function readGroupsMixed(items) {
    const groups = [];
    const defaultGroup = { rootName: null, files: [], dirs: new Set() };

    for (const item of items || []) {
      try {
        // @ts-ignore
        if (item && "getAsFileSystemHandle" in item) {
          // eslint-disable-next-line no-await-in-loop
          const handle = await item.getAsFileSystemHandle();
          if (handle) {
            if (handle.kind === "file") {
              const file = await handle.getFile();
              defaultGroup.files.push({ file, relativePath: "" });
              continue;
            }
            if (handle.kind === "directory") {
              const group = { rootName: handle.name, files: [], dirs: new Set() };
              // eslint-disable-next-line no-await-in-loop
              await walkDirHandle(handle, group, "");
              groups.push(group);
              continue;
            }
          }
        }
      } catch {}

      try {
        if (item && item.webkitGetAsEntry) {
          const single = await readGroupsWebkit([item]);
          if (single.groups.length) groups.push(...single.groups);
          if (single.defaultGroup.files.length)
            defaultGroup.files.push(...single.defaultGroup.files);
        }
      } catch {}
    }

    return { groups, defaultGroup };
  }

  function groupFromRawFiles(rawFiles) {
    const groupsMap = new Map();
    const defaultGroup = { rootName: null, files: [], dirs: new Set() };

    if (!rawFiles || !rawFiles.length) return { groups: [], defaultGroup };

    for (const f of Array.from(rawFiles)) {
      const wrp = f.webkitRelativePath || "";
      if (wrp && wrp.includes("/")) {
        const norm = wrp.replace(/\\/g, "/");
        const [root, ...rest] = norm.split("/");
        const rel = rest.join("/");
        if (!groupsMap.has(root)) {
          groupsMap.set(root, { rootName: root, files: [], dirs: new Set() });
        }
        const g = groupsMap.get(root);
        if (rel) {
          const parts = rel.split("/").slice(0, -1);
          let acc = "";
          for (const p of parts) {
            acc = acc ? `${acc}/${p}` : p;
            g.dirs.add(acc + "/");
          }
          g.files.push({ file: f, relativePath: rel });
        } else {
          g.files.push({ file: f, relativePath: "" });
        }
      } else {
        defaultGroup.files.push({ file: f, relativePath: "" });
      }
    }

    return { groups: Array.from(groupsMap.values()), defaultGroup };
  }

  function dedupeFiles(fileEntries) {
    const out = [];
    const seen = new Set();
    for (const fe of fileEntries) {
      if (!fe || !fe.file) continue;
      const f = fe.file;
      const key = `${f.name}|${f.size}|${f.lastModified}|${fe.relativePath || ""}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(fe);
    }
    return out;
  }

  // ---------- Drag & drop main
  useEffect(() => {
    const ref = mainRef.current;
    if (!ref) return;
    let dragTimeout = null;

    const handleDragOver = (e) => {
      e.preventDefault();
      setDragActive(true);
      if (dragTimeout) clearTimeout(dragTimeout);
    };
    const handleDragLeave = (e) => {
      e.preventDefault();
      dragTimeout = setTimeout(() => setDragActive(false), 80);
    };
    const handleDrop = async (e) => {
      e.preventDefault();
      setDragActive(false);

      const { items, files: rawFiles } = e.dataTransfer || {};
      setUploadError("");
      setUploading(true);
      uploadErrorsRef.current = [];

      let successCount = 0;

      try {
        const webkitOut = await readGroupsWebkit(items || []);
        const mixedOut = await readGroupsMixed(items || []);
        const rawOut = groupFromRawFiles(rawFiles || []);

        const groups =
          (webkitOut.groups && webkitOut.groups.length ? webkitOut.groups : mixedOut.groups) || [];

        const defaultFiles = dedupeFiles([
          ...(webkitOut.defaultGroup?.files || []),
          ...(mixedOut.defaultGroup?.files || []),
          ...(rawOut.defaultGroup?.files || []),
        ]);

        if (groups.length === 0 && defaultFiles.length === 0) {
          setUploadError(
            "Folder drag-and-drop isn’t supported by this browser here. Please use the ‘Upload Folder’ button."
          );
          return;
        }

        if (!parentId && groups.length === 0 && defaultFiles.length > 0) {
          setUploadError("Please select a destination folder before uploading.");
          return;
        }

        const processGroup = async (group) => {
          if (!isValidFolderName(group.rootName)) {
            uploadErrorsRef.current.push(
              `Skipped folder "${group.rootName}": name must be ≤ ${NAME_MAX} characters.`
            );
            return;
          }
          const rootFolder = await createChildFolder(group.rootName);
          if (!rootFolder) return;
          successCount++;

          for (const dir of group.dirs) {
            // eslint-disable-next-line no-await-in-loop
            const ok = await ensureNestedFolderPath(rootFolder.id, dir);
            if (ok) successCount++;
          }

          for (const { file, relativePath } of group.files) {
            const base = getFileBase(file.name);
            if (!isValidFileBase(base)) {
              uploadErrorsRef.current.push(
                `Skipped file "${file.name}": base name must be ≤ ${NAME_MAX} characters.`
              );
              continue;
            }
            const norm = (relativePath || "").replace(/\\/g, "/");
            const lastSlash = norm.lastIndexOf("/");
            const dirOnly = lastSlash >= 0 ? norm.slice(0, lastSlash) : "";
            // eslint-disable-next-line no-await-in-loop
            const target = await ensureNestedFolderPath(rootFolder.id, dirOnly);
            const targetId = target?.id ?? rootFolder.id;

            const formData = new FormData();
            formData.append("file", file);
            formData.append("project_id", targetId);
            try {
              // eslint-disable-next-line no-await-in-loop
              await axios.post(`${API}/files`, formData, {
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "multipart/form-data",
                },
              });
              successCount++;
            } catch {}
          }
        };

        for (const g of groups) {
          // eslint-disable-next-line no-await-in-loop
          await processGroup(g);
        }

        if (defaultFiles.length) {
          const dst = parentId ?? null;
          for (const { file, relativePath } of defaultFiles) {
            const base = getFileBase(file.name);
            if (!isValidFileBase(base)) {
              uploadErrorsRef.current.push(
                `Skipped file "${file.name}": base name must be ≤ ${NAME_MAX} characters.`
              );
              continue;
            }
            const formData = new FormData();
            formData.append("file", file);
            formData.append("project_id", dst ?? "");
            if (relativePath) formData.append("relativePath", relativePath);
            try {
              // eslint-disable-next-line no-await-in-loop
              await axios.post(`${API}/files`, formData, {
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "multipart/form-data",
                },
              });
              successCount++;
            } catch {}
          }
        }

        await fetchData();
      } finally {
        setUploading(false);
        if (successCount === 0) {
          setUploadError(
            uploadErrorsRef.current.length
              ? uploadErrorsRef.current.join(" ")
              : "File upload failed"
          );
        } else if (uploadErrorsRef.current.length) {
          setUploadError(
            `Uploaded ${successCount} item(s). ${uploadErrorsRef.current.length} skipped for name > ${NAME_MAX} chars.`
          );
        } else {
          setUploadError("");
        }
      }
    };

    ref.addEventListener("dragover", handleDragOver);
    ref.addEventListener("dragleave", handleDragLeave);
    ref.addEventListener("drop", handleDrop);
    return () => {
      ref.removeEventListener("dragover", handleDragOver);
      ref.removeEventListener("dragleave", handleDragLeave);
      ref.removeEventListener("drop", handleDrop);
      if (dragTimeout) clearTimeout(dragTimeout);
    };
  }, [
    mainRef,
    parentId,
    token,
    adminContext,
    adminUserId,
    createChildFolder,
    ensureNestedFolderPath,
    fetchData,
  ]);

  // ---------- Basic upload (buttons/inputs) — mirrors DnD for folder input
  const handleFileUpload = async (e, fileOverride = null, fileEntries = null) => {
    setUploadError("");
    uploadErrorsRef.current = [];
    try {
      // If a native input fired and includes folder structure, use the grouped flow
      if (e?.target?.files && e.target.files.length) {
        const { groups, defaultGroup } = groupFromRawFiles(e.target.files);

        if (groups.length > 0) {
          if (!parentId && defaultGroup.files.length > 0) {
            setUploadError("Please select a destination folder before uploading loose files.");
            return;
          }
          setUploading(true);
          let successCount = 0;

          try {
            for (const g of groups) {
              if (!isValidFolderName(g.rootName)) {
                uploadErrorsRef.current.push(
                  `Skipped folder "${g.rootName}": name must be ≤ ${NAME_MAX} characters.`
                );
                continue;
              }
              // eslint-disable-next-line no-await-in-loop
              const rootFolder = await createChildFolder(g.rootName);
              if (!rootFolder) continue;
              successCount++;

              for (const dir of g.dirs) {
                // eslint-disable-next-line no-await-in-loop
                const ok = await ensureNestedFolderPath(rootFolder.id, dir);
                if (ok) successCount++;
              }

              for (const { file, relativePath } of g.files) {
                const base = getFileBase(file.name);
                if (!isValidFileBase(base)) {
                  uploadErrorsRef.current.push(
                    `Skipped file "${file.name}": base name must be ≤ ${NAME_MAX} characters.`
                  );
                  continue;
                }
                const norm = (relativePath || "").replace(/\\/g, "/");
                const lastSlash = norm.lastIndexOf("/");
                const dirOnly = lastSlash >= 0 ? norm.slice(0, lastSlash) : "";
                // eslint-disable-next-line no-await-in-loop
                const target = await ensureNestedFolderPath(rootFolder.id, dirOnly);
                const targetId = target?.id ?? rootFolder.id;

                const formData = new FormData();
                formData.append("file", file);
                formData.append("project_id", targetId);
                try {
                  // eslint-disable-next-line no-await-in-loop
                  await axios.post(`${API}/files`, formData, {
                    headers: {
                      Authorization: `Bearer ${token}`,
                      "Content-Type": "multipart/form-data",
                    },
                  });
                  successCount++;
                } catch {}
              }
            }

            // Handle any loose files that came from the input (rare)
            if (defaultGroup.files.length) {
              if (!parentId) {
                uploadErrorsRef.current.push(
                  "Skipped loose files: please pick a destination folder."
                );
              } else {
                for (const { file, relativePath } of defaultGroup.files) {
                  const base = getFileBase(file.name);
                  if (!isValidFileBase(base)) {
                    uploadErrorsRef.current.push(
                      `Skipped file "${file.name}": base name must be ≤ ${NAME_MAX} characters.`
                    );
                    continue;
                  }
                  const formData = new FormData();
                  formData.append("file", file);
                  formData.append("project_id", parentId ?? "");
                  if (relativePath) formData.append("relativePath", relativePath);
                  // eslint-disable-next-line no-await-in-loop
                  await axios
                    .post(`${API}/files`, formData, {
                      headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "multipart/form-data",
                      },
                    })
                    .then(() => {
                      successCount++;
                    })
                    .catch(() => {});
                }
              }
            }

            await fetchData();
          } finally {
            setUploading(false);
            if (successCount === 0) {
              setUploadError(
                uploadErrorsRef.current.length
                  ? uploadErrorsRef.current.join(" ")
                  : "File upload failed"
              );
            } else if (uploadErrorsRef.current.length) {
              setUploadError(
                `Uploaded ${successCount} item(s). ${uploadErrorsRef.current.length} skipped for name > ${NAME_MAX} chars.`
              );
            } else {
              setUploadError("");
            }
            if (fileInputRef.current) fileInputRef.current.value = "";
            if (folderInputRef.current) folderInputRef.current.value = "";
          }
          return; // stop here for folder-input flow
        }

        // else: fall through to plain file flow using defaultGroup.files
        fileEntries = defaultGroup.files.map(({ file, relativePath }) => ({
          file,
          relativePath,
        }));
      }

      // Plain files flow (Upload File(s) button or provided overrides)
      let toUpload = [];
      if (fileEntries) {
        toUpload = fileEntries;
      } else if (fileOverride && fileOverride.length) {
        toUpload = Array.from(fileOverride).map((f) => ({
          file: f,
          relativePath: f.webkitRelativePath || "",
        }));
      } else if (e && e.target && e.target.files) {
        toUpload = Array.from(e.target.files).map((f) => ({
          file: f,
          relativePath: f.webkitRelativePath || "",
        }));
      }

      if (!toUpload.length) {
        setUploadError("No files selected.");
        return;
      }

      const isFolderUpload = toUpload.some(
        (f) => f.relativePath && f.relativePath.includes("/")
      );
      if (!parentId && !isFolderUpload) {
        setUploadError("No destination folder selected!");
        return;
      }

      setUploading(true);
      let successCount = 0;
      for (const entry of toUpload) {
        const base = getFileBase(entry.file.name);
        if (!isValidFileBase(base)) {
          uploadErrorsRef.current.push(
            `Skipped file "${entry.file.name}": base name must be ≤ ${NAME_MAX} characters.`
          );
          continue;
        }
        const formData = new FormData();
        formData.append("file", entry.file);
        formData.append("project_id", parentId ?? "");
        if (entry.relativePath) formData.append("relativePath", entry.relativePath);
        try {
          // eslint-disable-next-line no-await-in-loop
          await axios.post(`${API}/files`, formData, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          });
          successCount++;
        } catch {
          /* ignore per-file; we'll decide after loop */
        }
      }
      await fetchData();
      if (successCount === 0) {
        setUploadError(
          uploadErrorsRef.current.length
            ? uploadErrorsRef.current.join(" ")
            : "File upload failed"
        );
      } else if (uploadErrorsRef.current.length) {
        setUploadError(
          `Uploaded ${successCount} item(s). ${uploadErrorsRef.current.length} skipped for name > ${NAME_MAX} chars.`
        );
      } else {
        setUploadError("");
      }
    } catch {
      setUploadError("File upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
      if (folderInputRef.current) folderInputRef.current.value = "";
    }
  };

  const handleRename = async (item, isFolder) => {
    let newName = item.name;
    if (!isFolder) {
      const [base, ext] = getFileNameParts(item.name);
      const userInput = prompt(
        `Rename file (max ${NAME_MAX} chars, extension stays the same):`,
        base.slice(0, NAME_MAX)
      );
      if (!userInput || userInput.trim() === "" || userInput === base) return;
      if (userInput.length > NAME_MAX) {
        alert(`Name too long. Max ${NAME_MAX} characters.`);
        return;
      }
      newName = userInput + ext;
    } else {
      const userInput = prompt(
        `Rename folder (max ${NAME_MAX} chars):`,
        item.name.slice(0, NAME_MAX)
      );
      if (!userInput || userInput.trim() === "" || userInput === item.name) return;
      if (userInput.length > NAME_MAX) {
        alert(`Name too long. Max ${NAME_MAX} characters.`);
        return;
      }
      newName = userInput;
    }
    try {
      if (isFolder) {
        await axios.put(
          `${API}/projects/${item.id}`,
          { name: newName },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        updateFolder({
          ...item,
          name: newName,
          updated_at: new Date().toISOString(),
        });
      } else {
        await axios.put(
          `${API}/files/${item.id}`,
          { name: newName },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        updateFile({
          ...item,
          name: newName,
          updated_at: new Date().toISOString(),
        });
      }
      fetchData();
    } catch {
      setError("Rename failed");
    }
  };

  function getFileNameParts(filename) {
    const dot = filename.lastIndexOf(".");
    if (dot <= 0) return [filename, ""];
    return [filename.substring(0, dot), filename.substring(dot)];
  }

  const handleDelete = async (item, isFolder) => {
    if (
      !window.confirm(
        `Are you sure you want to delete "${item.name}"? This cannot be undone.`
      )
    )
      return;
    try {
      if (isFolder) {
        await axios.delete(`${API}/projects/${item.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        removeFolder(item.id);
      } else {
        await axios.delete(`${API}/files/${item.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        removeFile(item.id);
      }
      fetchData();
    } catch {
      setError("Delete failed");
    }
  };

  const handleDownload = async (item, isFolder) => {
    try {
      let url, name;
      if (isFolder) {
        url = `${API}/projects/${item.id}/download`;
        name = `${item.name}.zip`;
      } else {
        url = `${API}/files/${item.id}/download`;
        name = item.name;
      }
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(new Blob([res.data]));
      link.setAttribute("download", name);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      setError("Download failed");
    }
  };

  // START work: only trigger yellow flash for FILES (not folders)
  const handleStartWork = async (item, isFolder) => {
    try {
      await axios.post(
        `${API}/work/start`,
        {
          project_id: isFolder ? item.id : null,
          file_id: isFolder ? null : item.id,
          auto_stop: true,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchWorkingOn();
      if (!isFolder) {
        setHighlightId(item.id);
        setHighlightActive(true);
        if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
        highlightTimeoutRef.current = setTimeout(() => {
          setHighlightActive(false);
          setHighlightId(null);
        }, 5000);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Could not start work.");
    }
  };

  const handleStopWork = async () => {
    try {
      await axios.post(`${API}/work/stop`, {}, { headers: { Authorization: `Bearer ${token}` } });
      fetchWorkingOn();
    } catch (err) {
      setError(err.response?.data?.message || "Could not stop work.");
    }
  };

  const fetchWorkingOn = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/work/current`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.data.active) {
        setWorkingOn(null);
        return;
      }
      const entry = res.data.entry;
      let item = null,
        isFolder = false,
        parentToNav = null;

      if (entry.project_id) {
        try {
          item = folders[entry.project_id] || (await fetchFolder(entry.project_id, token));
        } catch {}
        if (!item) {
          try {
            await axios.get(`${API}/projects/${entry.project_id}`, {
              headers: { Authorization: `Bearer ${token}` },
            });
          } catch (e) {
            if (e.response && e.response.status === 404) {
              await axios.post(`${API}/work/stop`, {}, { headers: { Authorization: `Bearer ${token}` } });
              setWorkingOn(null);
              return;
            }
          }
          setWorkingOn(null);
          return;
        }
        isFolder = true;
        parentToNav = item ? item.parent_id : null;
      } else if (entry.file_id) {
        try {
          item = files[entry.file_id] || (await fetchFile(entry.file_id, token));
        } catch {}
        if (!item) {
          try {
            await axios.get(`${API}/files/${entry.file_id}`, {
              headers: { Authorization: `Bearer ${token}` },
            });
          } catch (e) {
            if (e.response && e.response.status === 404) {
              await axios.post(`${API}/work/stop`, {}, { headers: { Authorization: `Bearer ${token}` } });
              setWorkingOn(null);
              return;
            }
          }
          setWorkingOn(null);
          return;
        }
        isFolder = false;
        parentToNav = item ? item.project_id : null;
      }

      setWorkingOn({ ...entry, item, isFolder, parentToNav });
    } catch (e) {
      if (e.response && e.response.status === 404) {
        setWorkingOn(null);
      }
    }
  }, [folders, files, fetchFolder, fetchFile, token]);

  const handleBreadcrumbClick = (idx) => {
    if (idx === -1) setParentId(null);
    else setParentId(breadcrumbs[idx].id);
  };

  const handleTypeFilter = (type) => {
    setTypeFilter((prev) => (prev === type ? "" : type));
  };

  const getNextOrder = (cur) => (cur === "asc" ? "desc" : "asc");
  const arrow = (active, order) => (active ? (order === "asc" ? "▲" : "▼") : "");

  const handleFolderClick = (folder) => setParentId(folder.id);
  const handleFileClick = (file) => {
    if (file.project_id) setParentId(file.project_id);
  };

  useEffect(() => {
    if (highlightActive && highlightId) {
      setTimeout(() => {
        const el = document.getElementById(`row-${highlightId}`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 300);
    }
  }, [highlightActive, highlightId]);

  // ---------- Derived pagination slices
  const totalFolders = folderList.length;
  const totalFiles = fileList.length;
  const folderPages = Math.max(1, Math.ceil(totalFolders / folderPageSize));
  const filePages = Math.max(1, Math.ceil(totalFiles / filePageSize));
  const folderStart = (folderPage - 1) * folderPageSize;
  const fileStart = (filePage - 1) * filePageSize;
  const visibleFolders = folderList.slice(folderStart, folderStart + folderPageSize);
  const visibleFiles = fileList.slice(fileStart, fileStart + filePageSize);

  // editable page input helpers
  const digitsOnly = (v) => v.replace(/\D/g, "");
  const commitFolderPage = () => {
    let n = parseInt(folderPageStr, 10);
    if (isNaN(n)) n = 1;
    if (n < 1) n = 1;
    if (n > folderPages) n = folderPages;
    setFolderPage(n);
    setFolderPageStr(String(n));
  };
  const commitFilePage = () => {
    let n = parseInt(filePageStr, 10);
    if (isNaN(n)) n = 1;
    if (n < 1) n = 1;
    if (n > filePages) n = filePages;
    setFilePage(n);
    setFilePageStr(String(n));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar isAdmin={isAdmin ?? isAdminFromToken} />
      {dragActive && (
        <div className="fixed inset-0 bg-black bg-opacity-20 flex items-start justify-center z-50 pointer-events-none pt-32">
          <div className="bg-white border-2 border-blue-600 rounded-lg px-12 py-8 shadow-lg text-xl font-bold text-blue-700 opacity-100 pointer-events-none text-center">
            Drop files or folders here
            <div className="text-xs text-gray-500 font-normal mt-3">
              For uploading whole folders, please use the{" "}
              <span className="font-semibold">Upload Folder</span> button.<br />
              Drag &amp; drop supports files only in some browsers.
            </div>
          </div>
        </div>
      )}
      <div
        ref={mainRef}
        className={fullWidth ? "pt-0 w-full" : "pt-20 max-w-6xl mx-auto px-4"}
      >
        <h2 className="text-2xl font-bold mb-4">Projects</h2>

        <div className="flex flex-wrap gap-4 items-center mb-2">
          <input
            type="text"
            className="border border-gray-300 rounded p-2 w-56 bg-gray-100"
            placeholder="Search by name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select
            className="border border-gray-300 rounded p-2 bg-gray-100"
            value={searchScope}
            onChange={(e) => setSearchScope(e.target.value)}
          >
            <option value="current">Current folder</option>
            <option value="all">All folders below</option>
          </select>
          <div className="flex gap-2">
            <button
              className={`px-3 py-2 rounded border text-sm transition ${
                typeFilter === "folders"
                  ? "bg-blue-100 border-blue-400 text-blue-800"
                  : "bg-gray-100 border-gray-300 text-gray-400"
              }`}
              onClick={() => handleTypeFilter("folders")}
            >
              Folders
            </button>
            <button
              className={`px-3 py-2 rounded border text-sm transition ${
                typeFilter === "files"
                  ? "bg-blue-100 border-blue-400 text-blue-800"
                  : "bg-gray-100 border-gray-300 text-gray-400"
              }`}
              onClick={() => handleTypeFilter("files")}
            >
              Files
            </button>
          </div>
          <div className="flex gap-1 items-center">
            <label className="text-gray-700 text-sm font-medium">Date:</label>
            <select
              className="border border-gray-300 rounded p-2 bg-gray-100"
              value={dateType}
              onChange={(e) => setDateType(e.target.value)}
            >
              <option value="created_at">Created</option>
              <option value="updated_at">Last Edited</option>
            </select>
            <DatePicker
              selectsRange
              startDate={dateRange[0]}
              endDate={dateRange[1]}
              onChange={(dates) => setDateRange(dates)}
              isClearable
              placeholderText="Pick date or range"
              className="border border-gray-300 rounded p-2 bg-gray-100 w-44"
            />
          </div>
          {adminContext === "workers" && (
            <label className="flex items-center gap-2 ml-2 text-sm font-medium text-gray-700">
              <input
                type="checkbox"
                className="w-4 h-4"
                checked={activeOnly}
                onChange={(e) => setActiveOnly(e.target.checked)}
              />
              Show active only
            </label>
          )}
        </div>

        <div className="flex gap-2 items-center text-gray-600 mb-4 mt-2">
          <span
            className={`cursor-pointer hover:text-blue-600 ${
              !parentId ? "font-semibold underline" : ""
            }`}
            onClick={() => handleBreadcrumbClick(-1)}
          >
            Root
          </span>
          {breadcrumbs.map((b, idx) => (
            <span key={b.id} className="flex items-center gap-2">
              <span>/</span>
              <span
                className={`cursor-pointer hover:text-blue-600 ${
                  idx === breadcrumbs.length - 1 ? "font-semibold underline" : ""
                }`}
                onClick={() => handleBreadcrumbClick(idx)}
              >
                {b.name}
              </span>
            </span>
          ))}
        </div>

        <div className="flex gap-4 mb-6 mt-2">
          <input
            type="text"
            className="border border-gray-300 rounded p-2 bg-gray-100"
            placeholder="New folder name"
            value={newFolderName}
            onChange={(e) =>
              setNewFolderName(
                e.target.value.length > NAME_MAX
                  ? e.target.value.slice(0, NAME_MAX)
                  : e.target.value
              )
            }
            onKeyDown={(e) => {
              if (e.key === "Enter") handleCreateFolder();
            }}
          />
          <button
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition"
            onClick={handleCreateFolder}
          >
            <AiOutlinePlus className="inline-block mr-1" /> New Folder
          </button>
          <div className="flex gap-2">
            <button
              className="bg-gray-700 text-white px-4 py-2 rounded hover:bg-gray-800"
              type="button"
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
            >
              Upload File(s)
            </button>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              multiple
              onChange={(e) => handleFileUpload(e)}
            />
            <button
              className="bg-gray-700 text-white px-4 py-2 rounded hover:bg-gray-800"
              type="button"
              onClick={() => folderInputRef.current && folderInputRef.current.click()}
            >
              Upload Folder
            </button>
            <input
              ref={folderInputRef}
              type="file"
              className="hidden"
              multiple
              webkitdirectory="true"
              directory="true"
              onChange={(e) => handleFileUpload(e)}
            />
          </div>
          {uploading && <span className="ml-2 text-gray-500">Uploading…</span>}
        </div>

        {uploadError && <div className="mb-4 text-red-600 whitespace-pre-wrap">{uploadError}</div>}

        {workingOn && workingOn.item && (
          <div className="mb-4 flex items-center text-blue-800 font-semibold border border-blue-100 px-3 py-2 rounded bg-blue-50">
            <AiOutlinePlayCircle className="mr-2" />
            Currently working on:&nbsp;
            <button
              type="button"
              className="underline cursor-pointer hover:text-blue-900 bg-transparent p-0 min-w-0"
              onClick={handleWorkingOnClick}
            >
              <TruncateWithTooltip text={workingOn.item.name} className="underline" />
            </button>
            <button
              className="ml-4 px-3 py-1 rounded bg-red-500 text-white hover:bg-red-600"
              onClick={handleStopWork}
            >
              <AiOutlinePauseCircle className="inline-block mr-1" /> Stop
            </button>
          </div>
        )}

        {error && <div className="mb-4 text-red-600">{error}</div>}

        {/* --- FOLDERS TABLE --- */}
        {showFolders && (
          <div className="mb-4 border border-gray-300 rounded-lg overflow-hidden">
            <div className="flex items-center justify-between bg-gray-100 py-2 px-4 border-b border-gray-300 font-semibold text-gray-800">
              <span>Folders</span>
              <div className="flex items-center gap-2 text-sm font-normal">
                <span className="text-gray-600">
                  {totalFolders
                    ? `Showing ${folderStart + 1}-${Math.min(
                        folderStart + folderPageSize,
                        totalFolders
                      )} of ${totalFolders}`
                    : "No items"}
                </span>
                <select
                  className="border border-gray-300 rounded bg-white px-2 py-1"
                  value={folderPageSize}
                  onChange={(e) => {
                    setFolderPageSize(Number(e.target.value));
                    setFolderPage(1);
                  }}
                >
                  {[10, 20, 40, 60].map((n) => (
                    <option key={n} value={n}>
                      {n}/page
                    </option>
                  ))}
                </select>
                <div className="flex items-center gap-1">
                  <button
                    className="px-2 py-1 border rounded disabled:opacity-50"
                    onClick={() => setFolderPage((p) => Math.max(1, p - 1))}
                    disabled={folderPage <= 1}
                  >
                    Prev
                  </button>
                  {/* Editable current page: digits only, clamps on blur/Enter */}
                  <div className="flex items-center gap-1">
                    <input
                      aria-label="Current folder page"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      className="w-12 px-2 py-1 border rounded text-center"
                      value={folderPageStr}
                      onChange={(e) => setFolderPageStr(digitsOnly(e.target.value))}
                      onBlur={commitFolderPage}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") commitFolderPage();
                      }}
                    />
                    <span>/</span>
                    <span>{folderPages}</span>
                  </div>
                  <button
                    className="px-2 py-1 border rounded disabled:opacity-50"
                    onClick={() => setFolderPage((p) => Math.min(folderPages, p + 1))}
                    disabled={folderPage >= folderPages}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
            <table className="w-full bg-white table-fixed">
              {/* Align columns with Files via a shared colgroup (include a 'Size' col here but keep it invisible in UI) */}
              <colgroup>
                <col /> {/* Name (flex) */}
                <col style={{ width: "8rem" }} /> {/* Size placeholder */}
                <col style={{ width: "16rem" }} /> {/* Created */}
                <col style={{ width: "16rem" }} /> {/* Modified */}
                <col style={{ width: "9rem" }} /> {/* Actions */}
              </colgroup>
              <thead>
                <tr className="text-gray-600 border-b border-gray-200">
                  <th
                    className={`py-2 text-left pl-4 font-medium cursor-pointer select-none ${
                      folderSort.col === "name" ? "text-blue-700" : ""
                    }`}
                    onClick={() =>
                      setFolderSort((s) =>
                        s.col === "name"
                          ? { ...s, order: getNextOrder(s.order) }
                          : { col: "name", order: "asc" }
                      )
                    }
                  >
                    Name {arrow(folderSort.col === "name", folderSort.order)}
                  </th>
                  {/* invisible Size header to preserve column grid */}
                  <th
                    className="py-2 text-left font-medium text-transparent select-none"
                    aria-hidden="true"
                  >
                    Size
                  </th>
                  <th
                    className={`py-2 text-left font-medium cursor-pointer select-none whitespace-nowrap ${
                      folderSort.col === "created_at" ? "text-blue-700" : ""
                    }`}
                    onClick={() =>
                      setFolderSort((s) =>
                        s.col === "created_at"
                          ? { ...s, order: getNextOrder(s.order) }
                          : { col: "created_at", order: "asc" }
                      )
                    }
                  >
                    Created {arrow(folderSort.col === "created_at", folderSort.order)}
                  </th>
                  <th
                    className={`py-2 text-left font-medium cursor-pointer select-none whitespace-nowrap ${
                      folderSort.col === "updated_at" ? "text-blue-700" : ""
                    }`}
                    onClick={() =>
                      setFolderSort((s) =>
                        s.col === "updated_at"
                          ? { ...s, order: getNextOrder(s.order) }
                          : { col: "updated_at", order: "asc" }
                      )
                    }
                  >
                    Modified {arrow(folderSort.col === "updated_at", folderSort.order)}
                  </th>
                  <th className="py-2 text-left font-medium whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleFolders.length === 0 ? (
                  <tr>
                    <td className="py-4 pl-4 text-gray-400" colSpan={5}>
                      No folders found.
                    </td>
                  </tr>
                ) : (
                  visibleFolders.map((folder) => (
                    <tr
                      key={folder.id}
                      id={`row-${folder.id}`}
                      className={`border-b last:border-b-0 hover:bg-gray-50 ${
                        highlightActive && highlightId === folder.id ? "bg-yellow-100" : ""
                      }`}
                    >
                      <td className="py-2 pl-4">
                        <div className="flex items-center">
                          <button
                            type="button"
                            className="inline-flex items-center gap-2 text-blue-700 font-semibold hover:underline bg-transparent p-0 min-w-0 flex-1"
                            onClick={() => handleFolderClick(folder)}
                          >
                            <AiOutlineFolder className="shrink-0 text-xl text-yellow-500 align-text-bottom" />
                            <TruncateWithTooltip text={folder.name} />
                          </button>

                          {adminContext === "workers" &&
                            activeFolderCascade[folder.id] &&
                            activeFolderCascade[folder.id].length > 0 && (
                              <ActiveBadge users={activeFolderCascade[folder.id]} />
                            )}
                        </div>
                      </td>
                      {/* invisible size cell keeps grid */}
                      <td
                        className="py-2 whitespace-nowrap text-transparent select-none"
                        aria-hidden="true"
                      >
                        —
                      </td>
                      <td className="py-2 whitespace-nowrap">
                        {new Date(folder.created_at).toLocaleString()}
                      </td>
                      <td className="py-2 whitespace-nowrap">
                        {new Date(folder.updated_at).toLocaleString()}
                      </td>
                      <td className="py-2 whitespace-nowrap">
                        <div className="flex gap-2">
                          <button
                            className="text-blue-700 hover:text-blue-900"
                            title="Download ZIP"
                            onClick={() => handleDownload(folder, true)}
                          >
                            <AiOutlineDownload />
                          </button>
                          <button
                            className="text-gray-500 hover:text-blue-700"
                            title="Rename"
                            onClick={() => handleRename(folder, true)}
                          >
                            <AiOutlineEdit />
                          </button>
                          <button
                            className="text-red-600 hover:text-red-800"
                            title="Delete"
                            onClick={() => handleDelete(folder, true)}
                          >
                            <AiOutlineDelete />
                          </button>
                          {workingOn?.isFolder && workingOn.item?.id === folder.id ? (
                            <button
                              className="text-red-600 hover:text-red-800"
                              title="Stop working"
                              onClick={handleStopWork}
                            >
                              <AiOutlinePauseCircle />
                            </button>
                          ) : (
                            <button
                              className="text-green-700 hover:text-green-900"
                              title="Start working"
                              onClick={() => handleStartWork(folder, true)}
                            >
                              <AiOutlinePlayCircle />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* --- FILES TABLE --- */}
        {showFiles && (parentId || searchScope === "all") && (
          <div className="mb-10 border border-gray-300 rounded-lg overflow-hidden">
            <div className="flex items-center justify-between bg-gray-100 py-2 px-4 border-b border-gray-300 font-semibold text-gray-800">
              <span>Files</span>
              <div className="flex items-center gap-2 text-sm font-normal">
                <span className="text-gray-600">
                  {totalFiles
                    ? `Showing ${fileStart + 1}-${Math.min(
                        fileStart + filePageSize,
                        totalFiles
                      )} of ${totalFiles}`
                    : "No items"}
                </span>
                <select
                  className="border border-gray-300 rounded bg-white px-2 py-1"
                  value={filePageSize}
                  onChange={(e) => {
                    setFilePageSize(Number(e.target.value));
                    setFilePage(1);
                  }}
                >
                  {[10, 20, 40, 60].map((n) => (
                    <option key={n} value={n}>
                      {n}/page
                    </option>
                  ))}
                </select>
                <div className="flex items-center gap-1">
                  <button
                    className="px-2 py-1 border rounded disabled:opacity-50"
                    onClick={() => setFilePage((p) => Math.max(1, p - 1))}
                    disabled={filePage <= 1}
                  >
                    Prev
                  </button>
                  {/* Editable current page */}
                  <div className="flex items-center gap-1">
                    <input
                      aria-label="Current file page"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      className="w-12 px-2 py-1 border rounded text-center"
                      value={filePageStr}
                      onChange={(e) => setFilePageStr(digitsOnly(e.target.value))}
                      onBlur={commitFilePage}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") commitFilePage();
                      }}
                    />
                    <span>/</span>
                    <span>{filePages}</span>
                  </div>
                  <button
                    className="px-2 py-1 border rounded disabled:opacity-50"
                    onClick={() => setFilePage((p) => Math.min(filePages, p + 1))}
                    disabled={filePage >= filePages}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
            <table className="w-full bg-white table-fixed">
              {/* Same column grid as folders */}
              <colgroup>
                <col /> {/* Name (flex) */}
                <col style={{ width: "8rem" }} /> {/* Size */}
                <col style={{ width: "16rem" }} /> {/* Created */}
                <col style={{ width: "16rem" }} /> {/* Modified */}
                <col style={{ width: "9rem" }} /> {/* Actions */}
              </colgroup>
              <thead>
                <tr className="text-gray-600 border-b border-gray-200">
                  <th
                    className={`py-2 text-left pl-4 font-medium cursor-pointer select-none ${
                      fileSort.col === "name" ? "text-blue-700" : ""
                    }`}
                    onClick={() =>
                      setFileSort((s) =>
                        s.col === "name"
                          ? { ...s, order: getNextOrder(s.order) }
                          : { col: "name", order: "asc" }
                      )
                    }
                  >
                    Name {arrow(fileSort.col === "name", fileSort.order)}
                  </th>
                  <th
                    className={`py-2 text-left font-medium cursor-pointer select-none ${
                      fileSort.col === "size" ? "text-blue-700" : ""
                    }`}
                    onClick={() =>
                      setFileSort((s) =>
                        s.col === "size"
                          ? { ...s, order: getNextOrder(s.order) }
                          : { col: "size", order: "asc" }
                      )
                    }
                  >
                    Size {arrow(fileSort.col === "size", fileSort.order)}
                  </th>
                  <th
                    className={`py-2 text-left font-medium cursor-pointer select-none whitespace-nowrap ${
                      fileSort.col === "created_at" ? "text-blue-700" : ""
                    }`}
                    onClick={() =>
                      setFileSort((s) =>
                        s.col === "created_at"
                          ? { ...s, order: getNextOrder(s.order) }
                          : { col: "created_at", order: "asc" }
                      )
                    }
                  >
                    Created {arrow(fileSort.col === "created_at", fileSort.order)}
                  </th>
                  <th
                    className={`py-2 text-left font-medium cursor-pointer select-none whitespace-nowrap ${
                      fileSort.col === "updated_at" ? "text-blue-700" : ""
                    }`}
                    onClick={() =>
                      setFileSort((s) =>
                        s.col === "updated_at"
                          ? { ...s, order: getNextOrder(s.order) }
                          : { col: "updated_at", order: "asc" }
                      )
                    }
                  >
                    Modified {arrow(fileSort.col === "updated_at", fileSort.order)}
                  </th>
                  <th className="py-2 text-left font-medium whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleFiles.length === 0 ? (
                  <tr>
                    <td className="py-4 pl-4 text-gray-400" colSpan={5}>
                      No files found.
                    </td>
                  </tr>
                ) : (
                  visibleFiles.map((file) => (
                    <tr
                      key={file.id}
                      id={`row-${file.id}`}
                      className={`border-b last:border-b-0 hover:bg-gray-50 ${
                        highlightActive && highlightId === file.id ? "bg-yellow-100" : ""
                      }`}
                    >
                      <td className="py-2 pl-4">
                        <div className="flex items-center">
                          <button
                            type="button"
                            className="inline-flex items-center gap-2 text-blue-700 font-medium hover:underline bg-transparent p-0 min-w-0 flex-1"
                            onClick={() => handleFileClick(file)}
                          >
                            <AiOutlineFile className="shrink-0 text-xl text-blue-600 align-text-bottom" />
                            <TruncateWithTooltip text={file.name} />
                          </button>

                          {adminContext === "workers" && activeWorkMap.files[file.id] && (
                            <ActiveBadge users={activeWorkMap.files[file.id]} />
                          )}
                        </div>
                      </td>
                      <td className="py-2 whitespace-nowrap">
                        {file.size ? formatSize(file.size) : "-"}
                      </td>
                      <td className="py-2 whitespace-nowrap">
                        {new Date(file.created_at).toLocaleString()}
                      </td>
                      <td className="py-2 whitespace-nowrap">
                        {new Date(file.updated_at).toLocaleString()}
                      </td>
                      <td className="py-2 whitespace-nowrap">
                        <div className="flex gap-2">
                          <button
                            className="text-blue-700 hover:text-blue-900"
                            title="Download"
                            onClick={() => handleDownload(file, false)}
                          >
                            <AiOutlineDownload />
                          </button>
                          <button
                            className="text-gray-500 hover:text-blue-700"
                            title="Rename"
                            onClick={() => handleRename(file, false)}
                          >
                            <AiOutlineEdit />
                          </button>
                          <button
                            className="text-red-600 hover:text-red-800"
                            title="Delete"
                            onClick={() => handleDelete(file, false)}
                          >
                            <AiOutlineDelete />
                          </button>
                          {workingOn && !workingOn.isFolder && workingOn.item?.id === file.id ? (
                            <button
                              className="text-red-600 hover:text-red-800"
                              title="Stop working"
                              onClick={handleStopWork}
                            >
                              <AiOutlinePauseCircle />
                            </button>
                          ) : (
                            <button
                              className="text-green-700 hover:text-green-900"
                              title="Start working"
                              onClick={() => handleStartWork(file, false)}
                            >
                              <AiOutlinePlayCircle />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default ProjectsPage;
